import { useState } from "react";
import { Plus, Trash2, Edit, CheckCircle, XCircle, ChevronDown, Search, X, Play } from "lucide-react";
import { formatDate } from "../../utils/formatters";
import DeleteConfirmModal from "./DeleteConfirmModal";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TradesTab = ({
  trades,
  regularUsers,
  showTradeForm,
  setShowTradeForm,
  tradeForm,
  setTradeForm,
  showUserDropdown,
  setShowUserDropdown,
  selectedUserForTrade,
  setSelectedUserForTrade,
  editingTradePnl,
  setEditingTradePnl,
  pnlForm,
  setPnlForm,
  onCreateTrade,
  onDeleteTrade,
  onCloseTrade,
  onUpdatePnl,
  onUpdate,
  token
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [deleteModal, setDeleteModal] = useState(null);
  const [showAssetDropdown, setShowAssetDropdown] = useState(false);

  // Top 20 криптовалют + стратегии
  const topAssets = [
    // Топ 20 по капитализации
    "BTC / USDT",
    "ETH / USDT",
    "BNB / USDT",
    "XRP / USDT",
    "SOL / USDT",
    "ADA / USDT",
    "DOGE / USDT",
    "TRX / USDT",
    "AVAX / USDT",
    "LINK / USDT",
    "DOT / USDT",
    "MATIC / USDT",
    "SHIB / USDT",
    "LTC / USDT",
    "ATOM / USDT",
    "UNI / USDT",
    "XLM / USDT",
    "NEAR / USDT",
    "APT / USDT",
    "ARB / USDT",
  ];

  const strategyPairs = [
    // Стратегии
    "LTC / ADA / SEI",
    "ETH / SOL / AVAX",
    "BTC / ETH / BNB",
    "DOGE / SHIB / PEPE",
    "DOT / ATOM / NEAR",
    "LINK / UNI / AAVE",
    "XRP / XLM / ADA",
    "SOL / APT / SUI",
    "ARB / OP / MATIC",
    "AVAX / FTM / ONE",
  ];
  
  // Map user_id (UUID) to client_id (6-digit) for search-by-id
  const clientIdMap = Object.fromEntries(
    (regularUsers || []).map(u => [u.id, u.client_id || ""])
  );
  
  // Filter trades by search query (email, name, asset, user_id UUID, client_id)
  const filteredTrades = trades.filter(trade => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const clientId = clientIdMap[trade.user_id] || "";
    return (
      trade.user_email?.toLowerCase().includes(query) ||
      trade.user_name?.toLowerCase().includes(query) ||
      trade.asset_name?.toLowerCase().includes(query) ||
      trade.user_id?.toLowerCase().includes(query) ||
      clientId.toLowerCase().includes(query)
    );
  });

  // Filter users for dropdown
  const filteredUsersForTrade = regularUsers.filter(user => {
    if (!userSearchQuery) return true;
    const query = userSearchQuery.toLowerCase();
    return (
      user.email?.toLowerCase().includes(query) ||
      user.name?.toLowerCase().includes(query) ||
      user.client_id?.toLowerCase().includes(query) ||
      user.id?.toLowerCase().includes(query)
    );
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-chivo font-bold text-xl text-white">Управление сделками</h2>
        <button
          onClick={() => setShowTradeForm(!showTradeForm)}
          className="btn-primary flex items-center gap-2"
          data-testid="create-trade-btn"
        >
          <Plus size={18} />
          Создать сделку
        </button>
      </div>

      {showTradeForm && (
        <div className="bg-[#0A0A0A] rounded-lg border border-[#1F1F1F] p-6 mb-6">
          <h3 className="font-chivo font-bold text-white mb-4">Новая сделка</h3>
          <form onSubmit={onCreateTrade} className="grid md:grid-cols-2 gap-4">
            {/* User Select with Search */}
            <div className="relative md:col-span-2">
              <label className="block text-[#A1A1AA] text-sm mb-2">Пользователь *</label>
              <button
                type="button"
                onClick={() => {
                  setShowUserDropdown(!showUserDropdown);
                  if (!showUserDropdown) setUserSearchQuery("");
                }}
                className="w-full bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white text-left flex justify-between items-center hover:border-[#2E2E2E]/50 transition-colors"
                data-testid="user-select"
              >
                <span>{selectedUserForTrade ? `${selectedUserForTrade.name} (${selectedUserForTrade.email})` : "Выберите пользователя"}</span>
                <ChevronDown size={20} className={`text-[#A1A1AA] transition-transform ${showUserDropdown ? "rotate-180" : ""}`} />
              </button>
              {showUserDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[#0A0A0A] border border-[#1F1F1F] rounded z-10 max-h-80 overflow-hidden shadow-xl">
                  {/* Search input */}
                  <div className="p-3 border-b border-[#1F1F1F] sticky top-0 bg-[#0A0A0A]">
                    <div className="relative">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                      <input
                        type="text"
                        value={userSearchQuery}
                        onChange={(e) => setUserSearchQuery(e.target.value)}
                        placeholder="Поиск по ID, имени или email..."
                        className="w-full bg-[#000000] border border-[#1F1F1F] rounded pl-9 pr-8 py-2 text-white text-sm placeholder-[#A1A1AA] focus:border-white focus:outline-none"
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                      {userSearchQuery && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setUserSearchQuery("");
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    {userSearchQuery && (
                      <div className="text-xs text-[#A1A1AA] mt-2">
                        Найдено: {filteredUsersForTrade.length} из {regularUsers.length}
                      </div>
                    )}
                  </div>
                  {/* Users list */}
                  <div className="max-h-60 overflow-y-auto">
                    {filteredUsersForTrade.length === 0 ? (
                      <div className="px-4 py-6 text-center text-[#A1A1AA]">
                        {userSearchQuery ? "Пользователи не найдены" : "Нет пользователей"}
                      </div>
                    ) : (
                      filteredUsersForTrade.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => {
                            setTradeForm({ ...tradeForm, user_id: u.id });
                            setSelectedUserForTrade(u);
                            setShowUserDropdown(false);
                            setUserSearchQuery("");
                          }}
                          className="w-full px-4 py-3 text-left text-white hover:bg-[#000000] transition-colors flex justify-between items-center border-b border-[#1F1F1F]/30 last:border-b-0"
                        >
                          <div>
                            <div className="font-medium">{u.name}</div>
                            <div className="text-[#A1A1AA] text-sm">{u.email}</div>
                          </div>
                          <div className="text-[#EDEDED] font-mono text-sm">${u.balance?.toFixed(2) || "0.00"}</div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Asset Select with dropdown + manual input */}
            <div className="relative">
              <label className="block text-[#A1A1AA] text-sm mb-2">Актив *</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tradeForm.asset_name}
                  onChange={(e) => setTradeForm({ ...tradeForm, asset_name: e.target.value })}
                  placeholder="Введите или выберите актив..."
                  className="flex-1 bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white placeholder-[#A1A1AA] focus:border-white focus:outline-none"
                  required
                  data-testid="trade-asset-input"
                />
                <button
                  type="button"
                  onClick={() => setShowAssetDropdown(!showAssetDropdown)}
                  className="px-4 py-3 bg-[#000000] border border-[#1F1F1F] rounded text-[#EDEDED] hover:border-[#2E2E2E] transition-colors"
                >
                  <ChevronDown size={20} className={`transition-transform ${showAssetDropdown ? "rotate-180" : ""}`} />
                </button>
              </div>
              {showAssetDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[#0A0A0A] border border-[#1F1F1F] rounded z-20 max-h-80 overflow-y-auto shadow-xl">
                  <div className="p-2 border-b border-[#1F1F1F] text-[#EDEDED] text-xs font-bold">ТОП-20 КРИПТОВАЛЮТ</div>
                  {topAssets.map((asset) => (
                    <button
                      key={asset}
                      type="button"
                      onClick={() => {
                        setTradeForm({ ...tradeForm, asset_name: asset });
                        setShowAssetDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-white hover:bg-[#000000] transition-colors text-sm"
                    >
                      {asset}
                    </button>
                  ))}
                  <div className="p-2 border-t border-b border-[#1F1F1F] text-[#34D399] text-xs font-bold">СТРАТЕГИИ</div>
                  {strategyPairs.map((asset) => (
                    <button
                      key={asset}
                      type="button"
                      onClick={() => {
                        setTradeForm({ ...tradeForm, asset_name: asset });
                        setShowAssetDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-[#34D399] hover:bg-[#000000] transition-colors text-sm"
                    >
                      {asset}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <input
              type="number"
              step="0.01"
              value={tradeForm.investment}
              onChange={(e) => setTradeForm({ ...tradeForm, investment: e.target.value })}
              placeholder="Сумма инвестиций ($)"
              className="bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white placeholder-[#A1A1AA] focus:border-white focus:outline-none"
              required
              data-testid="trade-investment-input"
            />
            <input
              type="number"
              step="0.1"
              value={tradeForm.pnl_percent}
              onChange={(e) => setTradeForm({ ...tradeForm, pnl_percent: e.target.value })}
              placeholder="Целевой % к закрытию"
              className="bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white placeholder-[#A1A1AA] focus:border-white focus:outline-none"
              data-testid="trade-pnl-input"
            />
            <select
              value={tradeForm.status}
              onChange={(e) => setTradeForm({ ...tradeForm, status: e.target.value })}
              className="bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white focus:border-white focus:outline-none"
              data-testid="trade-status-select"
            >
              <option value="active">Активна</option>
              <option value="closed">Закрыта</option>
              <option value="scheduled">Отложенная</option>
            </select>
            {tradeForm.status === "scheduled" && (
              <div>
                <label className="block text-[#EDEDED] text-sm mb-2">Дата активации *</label>
                <input
                  type="datetime-local"
                  value={tradeForm.scheduled_date || ""}
                  onChange={(e) => setTradeForm({ ...tradeForm, scheduled_date: e.target.value })}
                  className="w-full bg-[#000000] border border-white/50 rounded px-4 py-3 text-white focus:border-white focus:outline-none [color-scheme:dark]"
                  required
                  data-testid="trade-scheduled-date-input"
                />
                <p className="text-xs text-[#A1A1AA] mt-1">Сделка автоматически активируется в это время</p>
              </div>
            )}
            <div>
              <label className="block text-[#A1A1AA] text-sm mb-2">Дата закрытия</label>
              <input
                type="datetime-local"
                value={tradeForm.close_date}
                onChange={(e) => setTradeForm({ ...tradeForm, close_date: e.target.value })}
                className="w-full bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white focus:border-white focus:outline-none [color-scheme:dark]"
                data-testid="trade-close-date-input"
              />
            </div>
            <div className="md:col-span-2 flex gap-4">
              <button type="submit" className="btn-primary" data-testid="submit-trade-btn">
                Создать
              </button>
              <button
                type="button"
                onClick={() => { setShowTradeForm(false); setSelectedUserForTrade(null); }}
                className="border border-[#1F1F1F] text-white py-3 px-6 rounded hover:bg-[#0A0A0A]"
              >
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-[#0A0A0A] rounded-lg border border-[#1F1F1F] overflow-hidden">
        {/* Search bar */}
        <div className="p-4 border-b border-[#1F1F1F]">
          <div className="relative max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск по ID, имени, email или активу..."
              className="w-full bg-[#000000] border border-[#1F1F1F] rounded pl-10 pr-10 py-2.5 text-white placeholder-[#A1A1AA] focus:border-white focus:outline-none"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
            )}
          </div>
          {searchQuery && (
            <div className="mt-2 text-sm text-[#A1A1AA]">
              Найдено: {filteredTrades.length} из {trades.length} сделок
            </div>
          )}
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="admin-trades-table">
            <thead>
              <tr className="border-b border-[#1F1F1F]">
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Пользователь</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Название</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Вклад/Доход</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">PnL</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Статус</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Дата закрытия</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Активация</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredTrades.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-[#A1A1AA]">
                    {searchQuery ? "Сделки не найдены" : "Нет сделок"}
                  </td>
                </tr>
              ) : (
                filteredTrades.map((trade) => {
                  const currentPnl = trade.current_pnl_percent || 0;
                  const currentIncome = trade.current_income || 0;
                  const isPositive = currentPnl >= 0;
                  
                  return (
                    <tr key={trade.id} className="border-b border-[#1F1F1F]/50 hover:bg-[#1A1A1A]/50">
                      <td className="py-4 px-4">
                        <div className="text-white text-sm">{trade.user_name || "—"}</div>
                        <div className="text-[#A1A1AA] text-xs">{trade.user_email}</div>
                      </td>
                      <td className="py-4 px-4 text-white">{trade.asset_name}</td>
                      <td className="py-4 px-4">
                        <div className="font-mono">
                          <span className="text-white">${trade.investment?.toFixed(2)}</span>
                          <span className="text-[#A1A1AA] mx-1">/</span>
                          <span className={isPositive ? 'text-[#34D399]' : 'text-[#F87171]'}>
                            {isPositive ? '+' : ''}${currentIncome.toFixed(2)} ({isPositive ? '+' : ''}{currentPnl.toFixed(1)}%)
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        {editingTradePnl === trade.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              step="0.1"
                              value={pnlForm}
                              onChange={(e) => setPnlForm(e.target.value)}
                              className="bg-[#000000] border border-white rounded px-2 py-1 text-white w-20"
                              placeholder="%"
                              autoFocus
                            />
                            <button
                              onClick={() => onUpdatePnl(trade.id)}
                              className="text-[#34D399] hover:text-[#34D399]/70 p-1"
                            >
                              <CheckCircle size={16} />
                            </button>
                            <button
                              onClick={() => { setEditingTradePnl(null); setPnlForm(""); }}
                              className="text-[#A1A1AA] hover:text-white p-1"
                            >
                              <XCircle size={16} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className={`font-mono ${isPositive ? 'text-[#34D399]' : 'text-[#F87171]'}`}>
                              {isPositive ? '+' : ''}{currentPnl.toFixed(2)}%
                            </span>
                            {trade.status === "active" && (
                              <button
                                onClick={() => { setEditingTradePnl(trade.id); setPnlForm(currentPnl.toString()); }}
                                className="text-[#EDEDED] hover:text-[#EDEDED]/70 p-1"
                                title="Изменить PnL"
                              >
                                <Edit size={14} />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        {trade.status === "active" ? (
                          <span className="inline-flex items-center gap-1 bg-blue-500/15 text-blue-500 px-3 py-1 rounded text-sm whitespace-nowrap">
                            В работе
                          </span>
                        ) : trade.status === "scheduled" ? (
                          <span className="inline-flex items-center gap-1 bg-white/15 text-[#EDEDED] px-3 py-1 rounded text-sm whitespace-nowrap">
                            Отложенная
                          </span>
                        ) : trade.status === "pending" ? (
                          <span className="inline-flex items-center gap-1 bg-white/15 text-[#EDEDED] px-3 py-1 rounded text-sm whitespace-nowrap">
                            В ожидании
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 bg-[#34D399]/15 text-[#34D399] px-3 py-1 rounded text-sm whitespace-nowrap">
                            Выполнено
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-4 font-mono text-[#A1A1AA] text-sm">{formatDate(trade.close_date)}</td>
                      <td className="py-4 px-4 font-mono text-sm">
                        {trade.status === "scheduled" && trade.scheduled_date ? (
                          <span className="text-[#EDEDED]">{formatDate(trade.scheduled_date)}</span>
                        ) : trade.activated_at ? (
                          <span className="text-[#A1A1AA]">{formatDate(trade.activated_at)}</span>
                        ) : (
                          <span className="text-[#52525B]">—</span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex gap-2">
                          {trade.status === "active" && (
                            <button
                              onClick={() => onCloseTrade(trade.id)}
                              className="text-[#34D399] hover:text-[#34D399]/70 p-1"
                              title="Закрыть"
                            >
                              <CheckCircle size={18} />
                            </button>
                          )}
                          {trade.status === "scheduled" && (
                            <button
                              onClick={async () => {
                                try {
                                  await axios.put(
                                    `${API}/mnro/trades/${trade.id}`,
                                    { status: "active" },
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  toast.success("Сделка активирована");
                                  onUpdate();
                                } catch(e) { toast.error(e.response?.data?.detail || "Ошибка"); }
                              }}
                              className="text-blue-400 hover:text-blue-300 p-1"
                              title="Активировать сейчас"
                            >
                              <Play size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => setDeleteModal(trade)}
                            className="text-[#F87171] hover:text-[#F87171]/70 p-1"
                            title="Удалить"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <DeleteConfirmModal
          isOpen={!!deleteModal}
          onClose={() => setDeleteModal(null)}
          onConfirm={() => onDeleteTrade(deleteModal.id)}
          title="Удаление сделки"
          itemName={`${deleteModal.asset_name} (${deleteModal.user_email})`}
          itemType="сделку"
          warningText="Сделка будет удалена. Если сделка активна, средства вернутся на баланс пользователя."
          confirmText="УДАЛИТЬ"
        />
      )}
    </div>
  );
};

export default TradesTab;
